/**
 * MEETING CAPTURE — the Elevate rail card, available on ANY screen (#998).
 *
 * Christopher, 2026-08-09: *"I want to see it part of the elevate panel and ui
 * experience."* This is the second half of the blend: a standalone drawer panel
 * (like nav and copilot history — it is a lens on YOUR work, not on the record
 * on screen) that lets you pick a meeting and log a decision, an action or an
 * open point to it from wherever you are.
 *
 * ── deterministic first, AI optional (the mbn constraint) ───────────────────
 * The whole card works with NO copilot: pick a meeting, type, add. That is the
 * demo path on mbn, where no provider is wired. The paste box + ✦ extract are an
 * ACCELERATOR behind the same "Copilot not configured" guard SmartPaste uses —
 * when there is no provider it degrades to the deterministic fields, never a
 * dead error.
 *
 * ── shared controls throughout (#964) ──────────────────────────────────────
 * The meeting and the assignee are both `RefPicker` (the console's shared ref
 * control over the shared RelationField), the shared DatePicker for due, and the writes
 * are the governed engagements verbs (capture.ts) — no bespoke picker, no raw
 * insert.
 */
import { useState } from 'react'
import { Button, DatePicker, Pill, useToast, cx } from '@solum/ui'
import { RefPicker } from '../form/RefPicker'
import { useRecordDecision, useAssignAction, useRaiseOpenPoint } from './capture'
import { useSmartExtractStructured, type ExtractedItem } from './aiExtract'
import s from './MeetingCapture.module.css'

type Kind = 'decision' | 'action' | 'open_point'
const KINDS: { key: Kind; label: string; glyph: string }[] = [
  { key: 'decision', label: 'Decision', glyph: '✓' },
  { key: 'action', label: 'Action', glyph: '☑' },
  { key: 'open_point', label: 'Open point', glyph: '◔' },
]

export function MeetingCapture() {
  const toast = useToast()
  const [meetingId, setMeetingId] = useState<string | null>(null)
  const [kind, setKind] = useState<Kind>('decision')

  // one text field drives the primary of whichever kind is active
  const [text, setText] = useState('')
  const [assignee, setAssignee] = useState<string | null>(null)
  const [due, setDue] = useState('')

  const decision = useRecordDecision(meetingId)
  const action = useAssignAction(meetingId)
  const openPoint = useRaiseOpenPoint(meetingId)
  const busy = decision.isPending || action.isPending || openPoint.isPending

  const reset = () => { setText(''); setAssignee(null); setDue('') }

  const add = () => {
    const t = text.trim()
    if (!meetingId || !t) return
    const done = (what: string) => { toast.success(`${what} captured`); reset() }
    if (kind === 'decision') decision.mutate({ statement: t }, { onSuccess: () => done('Decision'), onError: (e) => toast.error('Not captured', String((e as Error).message)) })
    else if (kind === 'action') action.mutate({ title: t, assignee, due }, { onSuccess: () => done('Action'), onError: (e) => toast.error('Not captured', String((e as Error).message)) })
    else openPoint.mutate({ title: t, due }, { onSuccess: () => done('Open point'), onError: (e) => toast.error('Not captured', String((e as Error).message)) })
  }

  return (
    <div className={s.wrap} data-testid="meeting-capture">
      <div className={s.head}>
        <span className={s.title}>Capture to a meeting</span>
        <Pill tone="brand">Elevate</Pill>
      </div>
      <p className={s.sub}>Pick a meeting, then log a decision, an action or an open point — from anywhere.</p>

      {/* the target meeting — a shared RefPicker over engagements_engagement.
          A group heading (not a <label>): the picker is a button-widget, not a
          labelable input, so it carries its own accessible name via ariaLabel. */}
      <span className={s.k}>Meeting</span>
      <RefPicker target={{ entity: 'engagements_engagement', label: 'meeting' }} ariaLabel="Meeting"
        value={meetingId} onChange={(id) => setMeetingId(id)} testid="capture-meeting" />

      {!meetingId ? (
        <p className={s.hint} data-testid="capture-need-meeting">Choose a meeting to capture to.</p>
      ) : (
        <>
          {/* which part of the anatomy */}
          <div className={s.kinds} role="tablist" aria-label="What to capture">
            {KINDS.map((k) => (
              <button key={k.key} role="tab" aria-selected={kind === k.key}
                className={cx(s.kind, kind === k.key && s.kindOn)} data-testid={`capture-kind-${k.key}`}
                onClick={() => setKind(k.key)}>{k.glyph} {k.label}</button>
            ))}
          </div>

          <label className={s.k} htmlFor="capture-text">{kind === 'decision' ? 'What was decided' : kind === 'action' ? 'What needs doing' : 'The open question'}</label>
          <textarea id="capture-text" className={s.text} data-testid="capture-text" rows={2}
            value={text} onChange={(e) => setText(e.target.value)}
            placeholder={kind === 'decision' ? 'e.g. Ship the pilot in Q3' : kind === 'action' ? 'e.g. Send the revised pricing' : 'e.g. Who signs the DPA?'} />

          {kind === 'action' && (
            <>
              <span className={s.k}>Assignee</span>
              <RefPicker target={{ entity: 'core_person', label: 'person' }} ariaLabel="Assignee"
                value={assignee} onChange={(id) => setAssignee(id)} testid="capture-assignee" />
            </>
          )}
          {(kind === 'action' || kind === 'open_point') && (
            <>
              <span className={s.k} id="capture-due-label">Due</span>
              <DatePicker id="capture-due" aria-labelledby="capture-due-label"
                data-testid="capture-due" value={due} onChange={setDue} />
            </>
          )}

          <div className={s.actions}>
            <Button size="sm" variant="primary" data-testid="capture-add" disabled={!text.trim() || busy} loading={busy} onClick={add}>
              Add {KINDS.find((k) => k.key === kind)!.label.toLowerCase()}
            </Button>
          </div>

          <PasteAccelerator onUse={(item) => { setKind(item.kind); setText(item.title); if (item.due) setDue(item.due) }} />
        </>
      )}
    </div>
  )
}

/**
 * The paste box (#998 slice 4). Deterministic-first: paste notes, and without a
 * provider the ✦ Extract button says "Copilot not configured" and you fill the
 * fields yourself — the card still works. With a provider it extracts structured
 * decisions + assigned actions you confirm-then-write into the fields above. The
 * guard is the SAME one InteractionsPage's SmartPaste uses (aiExtract.ts).
 */
function PasteAccelerator({ onUse }: { onUse: (item: ExtractedItem) => void }) {
  const [text, setText] = useState('')
  const [open, setOpen] = useState(false)
  const extract = useSmartExtractStructured()
  const [items, setItems] = useState<ExtractedItem[] | null>(null)
  const toast = useToast()

  const run = () => {
    extract.mutate({ text }, {
      onSuccess: (v) => setItems(v),
      onError: (e) => toast.error('Copilot not configured', String((e as Error).message)),
    })
  }

  return (
    <div className={s.paste} data-testid="capture-paste">
      <button className={s.pasteToggle} data-testid="capture-paste-toggle" onClick={() => setOpen((v) => !v)}>
        ✦ Paste notes {open ? '−' : '+'}
      </button>
      {open && (
        <>
          <textarea className={s.text} rows={3} data-testid="capture-paste-text" value={text}
            onChange={(e) => setText(e.target.value)} placeholder="Paste a meeting note or email — extract decisions & actions, then confirm each." />
          <div className={s.actions}>
            <Button size="sm" variant="secondary" data-testid="capture-paste-extract"
              disabled={!text.trim() || extract.isPending} loading={extract.isPending} onClick={run}>✦ Extract</Button>
          </div>
          {items && items.length === 0 && <p className={s.hint}>Nothing structured found — type it above instead.</p>}
          {items && items.map((it, i) => (
            <div key={i} className={s.suggest} data-testid="capture-suggest">
              <span className={s.suggestKind}>{it.kind.replace('_', ' ')}</span>
              <span className={s.suggestTitle}>{it.title}</span>
              <Button size="sm" variant="ghost" data-testid="capture-suggest-use" onClick={() => onUse(it)}>Use</Button>
            </div>
          ))}
        </>
      )}
    </div>
  )
}
